




































































































































































import { Component, Mixins, Inject } from "vue-property-decorator";
import {
  IBasketProviderData,
  IBasketProviderMethods
} from "@/models/providers/baskets";
import CheckoutProvider from "@/components/app/global/baskets/basket/checkoutProvider.vue";
import BasketFields from "@/components/app/global/baskets/basket/shared/basketFields.vue";
import Context from "@/mixins/context";
import { IShopProviderMethods } from "@/models/providers/basket/shop";
import {
  IProvisionProviderData,
  IProvisionProviderMethods
} from "@/models/providers/provisioning";
import HasValidation from "@/mixins/HasValidation";
import { ValidationObserver } from "vee-validate";
import _ from "@/boot/lodash";
import { GatewayContext } from "@/data/enums/gateway";
import { ICheckoutProviderData } from "@/models/providers/checkout";

@Component({
  components: {
    "basket-fields": BasketFields,
    "checkout-provider": CheckoutProvider,
    "brand-logo": () => import("@/components/app/global/brand/logo.vue"),
    "checkout-button": () =>
      import("@/components/app/global/baskets/basket/checkoutButton.vue"),
    "checkout-section": () =>
      import("@/components/app/global/baskets/basket/checkoutSection.vue"),
    "checkout-trust-messaging": () =>
      import(
        "@/components/app/global/baskets/basket/checkoutTrustMessaging.vue"
      ),
    "express-skeleton": () => import("@/skeleton/order/express.vue"),
    "one-page-checkout-summary": () =>
      import(
        "@/components/app/global/baskets/basket/onePageCheckoutSummary.vue"
      ),
    "basket-avatar": () =>
      import("@/components/app/global/baskets/basket/basketAvatar.vue"),
    "basket-notifications": () =>
      import("@/components/app/global/baskets/basket/basketNotifications.vue"),
    "billable-entities-provider": () =>
      import("@/components/app/global/client/billableEntitiesProvider.vue"),
    "billable-entities": () =>
      import("@/components/app/global/client/billableEntities.vue"),
    "billable-entities-control": () =>
      import("@/components/app/global/client/billableEntitiesControl.vue"),
    "client-authentication": () =>
      import(
        "@/components/app/global/baskets/basket/express/authentication/clientAuthentication.vue"
      ),
    "basket-provision-warnings": () =>
      import(
        "@/components/app/global/baskets/basket/shared/provisionWarnings.vue"
      ),
    "select-payment-method": () =>
      import("@/components/app/global/payments/selectPaymentMethod/index.vue"),
    "guest-email": () =>
      import("@/components/app/global/baskets/basket/checkout/guestEmail.vue"),
    "continue-shopping-button": () =>
      import(
        "@/components/app/global/baskets/basket/shared/continueShoppingButton.vue"
      ),
    "cprod-billable-entity-address-search": () =>
      import("@/components/app/global/client/addressSearch.vue")
  }
})
export default class BasketOnePageView extends Mixins(Context, HasValidation) {
  // Refs

  public declare $refs: {
    observer: InstanceType<typeof ValidationObserver>;
    basketFields: BasketFields;
    checkoutProvider: CheckoutProvider;
  };

  // Inject

  @Inject({ from: "basketProviderData" })
  public readonly $basketProviderData!: () => IBasketProviderData;

  @Inject({ from: "provisionProviderData" })
  public readonly $provisionProviderData!: () => IProvisionProviderData;

  @Inject({ from: "basketProviderMethods" })
  public readonly $basketMethods!: IBasketProviderMethods;

  @Inject({ from: "provisionProviderMethods" })
  public readonly $provisionMethods!: IProvisionProviderMethods;

  @Inject({ from: "shopProviderMethods" })
  public readonly $shopMethods!: IShopProviderMethods;

  // Computed

  public get $basketData() {
    return this.$basketProviderData();
  }

  public get $provisionData() {
    return this.$provisionProviderData();
  }

  public get basketAddressId() {
    return this.$basketData.basket.address_id;
  }

  public get basketCompanyId() {
    return this.$basketData.basket.company_id;
  }

  public get basketClientId() {
    return this.$basketData.client?.id || this.$basketData.clientId;
  }

  // Methods

  public getSelectPaymentMethodBindings($checkoutData: ICheckoutProviderData) {
    return {
      accountId: this.$basketData.accountId,
      autoSelectFirstStoredPaymentMethod: !$checkoutData.paymentDetailIdFromExternalAuth,
      brandId: this.$basketData.basketBrandId,
      canChangeManualPaymentamount: this.isAdmin,
      cardHolderName: this.$basketData.client?.fullname,
      clientId: this.$basketData.clientId,
      currencyId: this.$basketData.currencyId,
      currencyCode: this.$basketData.currencyCode,
      excludedGatewayStoreTypes: $checkoutData.excludedGatewayStoreTypes,
      externalAuthReturnLocation: $checkoutData.externalAuthReturnLocation,
      gatewayContext: GatewayContext.PAY,
      hasManualpayment: this.isAdmin,
      hasPartialPayment: $checkoutData.hasPartialPayment,
      hasPayLater: $checkoutData.hasPayLater,
      isDisabled: this.$basketData.isProcessing,
      preselectedStoredPaymentMethodId:
        $checkoutData.paymentDetailIdFromExternalAuth,
      totalAmount: this.$basketData.totalAmountToPay,
      value: $checkoutData.paymentMethod
    };
  }

  public async validateBeforeSubmit() {
    this.$basketMethods.set("isProcessing", true);
    try {
      // Validate form
      if (!(await this.validate()))
        throw this.$t("_sentence.form_is_not_valid");
      // Check for provision field errors
      const { hasProvisioning, provisionErrors } = this.$provisionData;
      if (hasProvisioning && !this.$_.isEmpty(provisionErrors))
        throw this.$t("_sentence.basket.items_have_invalid_data");
      // Save basket fields
      await this.$refs.basketFields?.saveBasketFieldsData();
      // Do checkout
      await this.$refs.checkoutProvider.doCheckout();
    } catch (error) {
      if (this.$store.getters["api/is409"](error)) {
        try {
          await this.$refs.checkoutProvider.handle409ExistingInvoice(error);
          return;
        } catch (error) {
          // silent catch
        }
      }

      this.$store.dispatch("api/handleValidationError", {
        error,
        vm: this
      });

      /** Here we programmatically scroll to the basket fields element if there
       * are any custom field errors. `setTimeout` is required as there's a
       * small delay in `observer?.errors` updating to include the error(s). */

      setTimeout(() => {
        const hasCustomFieldErrors = _.some(
          this.validationErrors,
          (err, key) => !!err.length && key.startsWith("custom_fields")
        );
        if (hasCustomFieldErrors) this.$scrollTo(this.$refs.basketFields.$el);
      }, 50);
    } finally {
      this.$basketMethods.set("isProcessing", false);
    }
  }

  // Life-cycle methods

  public created() {
    if (this.$basketData.isNewBasket) {
      return this.$shopMethods.goToShop();
    }

    if (!this.$basketData.isEmptyBasket) {
      this.$provisionMethods.checkConfiguration(this.$basketData.basketId);
    }
  }
}
